import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaWallet, FaChartPie, FaInfoCircle, FaQuestionCircle, FaCheckCircle, FaLightbulb, FaQuestion } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const NetWorthCalculator = () => {
  const [formData, setFormData] = useState({
    assets: '',
    liabilities: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const calculateNetWorth = () => {
    const { assets, liabilities } = formData;

    if (!assets || !liabilities) {
      setError('Please fill in all required fields.');
      return;
    }

    const netWorth = parseFloat(assets) - parseFloat(liabilities);
    setResults({ netWorth: netWorth.toFixed(2) });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const series = [
    {
      name: 'Assets',
      data: [parseFloat(formData.assets) || 0]
    },
    {
      name: 'Liabilities',
      data: [parseFloat(formData.liabilities) || 0]
    },
    {
      name: 'Net Worth',
      data: [results ? parseFloat(results.netWorth) : 0]
    }
  ];

  return (
    <div className="calculator-container bg-gradient-to-r from-purple-500 to-pink-500 p-4 text-white">
      <Helmet>
        <title>Net Worth Calculator</title>
        <meta name="description" content="Calculate your net worth with our easy-to-use calculator." />
      </Helmet>

      <div className="calculator-header text-center mb-8">
        <FaWallet className="calculator-icon text-4xl text-white" />
        <h1 className="text-3xl font-bold text-black mb-2">Net Worth Calculator</h1>
        <p className="text-gray-700">Calculate your financial health</p>
      </div>

      <div className="calculator-content max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <div className="calculator-form-section">
          <form onSubmit={(e) => e.preventDefault()} className="calculator-form space-y-6">
            <div className="input-group">
              <label htmlFor="assets" className="block text-gray-700 font-bold mb-2">
                <FaWallet className="inline-block mr-2 text-gray-700" /> Total Assets
              </label>
              <input
                type="number"
                id="assets"
                name="assets"
                value={formData.assets}
                onChange={handleChange}
                placeholder="Enter total assets"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
              <span className="info-icon">
                <FaInfoCircle title="Total value of all your assets" className="text-gray-700 hover:text-purple-500 transition-colors duration-200" />
              </span>
            </div>

            <div className="input-group">
              <label htmlFor="liabilities" className="block text-gray-700 font-bold mb-2">
                <FaWallet className="inline-block mr-2 text-gray-700" /> Total Liabilities
              </label>
              <input
                type="number"
                id="liabilities"
                name="liabilities"
                value={formData.liabilities}
                onChange={handleChange}
                placeholder="Enter total liabilities"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
              <span className="info-icon">
                <FaInfoCircle title="Total amount of all your liabilities" className="text-gray-700 hover:text-purple-500 transition-colors duration-200" />
              </span>
            </div>

            <button
              onClick={calculateNetWorth}
              className="calculate-button w-full bg-purple-500 text-white rounded-md py-3 px-6 font-semibold hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition-all duration-200"
            >
              Calculate Net Worth
            </button>

            {error && (
              <div className="error-message bg-red-50 text-red-600 rounded-md p-3 mt-4">
                {error}
              </div>
            )}
          </form>
        </div>

        {results && (
          <div className="results-section mt-8">
            <div className="results-header flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Results</h2>
              <FaChartPie className="results-icon text-3xl text-gray-900" />
            </div>
            
            <div className="results-chart bg-white bg-opacity-90 rounded-lg p-6">
              <Chart
                options={{
                  chart: {
                    type: 'donut',
                    height: 350,
                    toolbar: {
                      show: false
                    },
                    animations: {
                      enabled: true,
                      easing: 'easeinout',
                      speed: 800
                    }
                  },
                  plotOptions: {
                    pie: {
                      donut: {
                        labels: {
                          show: true,
                          name: {
                            show: true,
                            fontSize: '20px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontWeight: 600,
                            color: '#333',
                            offsetY: -10
                          },
                          value: {
                            show: true,
                            fontSize: '14px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontWeight: 400,
                            color: '#333',
                            offsetY: 16,
                            formatter: (value) => formatCurrency(value)
                          }
                        }
                      }
                    }
                  },
                  dataLabels: {
                    enabled: false
                  },
                  stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                  },
                  legend: {
                    show: true,
                    position: 'bottom',
                    horizontalAlign: 'center',
                    offsetX: 0,
                    offsetY: 0,
                    fontSize: '14px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 400,
                    labels: {
                      colors: '#333'
                    }
                  },
                  colors: ['#4F46E5', '#EF4444', '#10B981']
                }}
                series={series.map(item => item.data[0])}
                type="donut"
                height={350}
              />
            </div>

            <div className="results-details mt-6 bg-green-500 bg-opacity-90 rounded-lg p-6">
              <div className="result-item flex items-center justify-between">
                <div className="flex items-center">
                  <FaWallet className="mr-3 text-gray-900" /> Net Worth
                </div>
                <div className="text-gray-900 font-semibold">
                  {formatCurrency(parseFloat(results.netWorth))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calculator-features mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features & Benefits</h2>
        <div className="features-grid grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaLightbulb className="feature-icon text-3xl text-yellow-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Comprehensive Calculation</h3>
            <p className="text-gray-700">Calculate your net worth by subtracting liabilities from assets.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaChartPie className="feature-icon text-3xl text-green-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Visual Representation</h3>
            <p className="text-gray-700">See a pie chart representation of your assets, liabilities, and net worth.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaCheckCircle className="feature-icon text-3xl text-blue-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Financial Insight</h3>
            <p className="text-gray-700">Gain insight into your financial health with a clear net worth calculation.</p>
          </div>
        </div>
      </div>

      <div className="how-to-use mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Use</h2>
        <ol className="usage-steps list-decimal pl-5 space-y-3">
          <li className="text-gray-700">Enter the total value of your assets</li>
          <li className="text-gray-700">Enter the total amount of your liabilities</li>
          <li className="text-gray-700">Click "Calculate Net Worth" to see your results</li>
        </ol>
      </div>

      <div className="faq-section mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
        <div className="faq-grid grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is net worth?</h3>
            <p className="text-gray-700">Net worth is the difference between your total assets and total liabilities.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Why is net worth important?</h3>
            <p className="text-gray-700">Net worth provides a snapshot of your financial health and helps in financial planning.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">How can I increase my net worth?</h3>
            <p className="text-gray-700">You can increase your net worth by increasing your assets or reducing your liabilities.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What are assets and liabilities?</h3>
            <p className="text-gray-700">Assets are things you own that have value, while liabilities are debts or obligations you owe.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetWorthCalculator;